import { useEffect, useRef } from 'react'
import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js'
import { useFrame, useThree } from '@react-three/fiber'
import { diagnosticMorphTargets } from '../utils/MorphTargetDiagnostic.js'
import { setContentFramer, fitCameraToSphere } from '../lib/viewportFraming'
import { graphState } from '../lib/graphState.js'
import { toProxiedAssetUrl } from '../lib/characterAssetUrl.js'
import { faceGraphState } from '../lib/faceGraphState.js'
import { evaluateGraph, evaluatePostMixer, applyWeights, applyRootDrive, updateSMTransitions, clearStickyRandomsInNodes } from '../lib/GraphEvaluator.js'
import { getCurrentArkit } from '../network/AudioAnimationBuffer.js'
import { debugState } from '../lib/debugState.js'
import { smRuntime } from '../lib/smRuntime.js'
import { faceEvalResult } from '../lib/faceEvalResult.js'
import { setVariable, variableValues } from '../lib/animVariables.js'
import { tracePhase } from '../lib/perfProbe.js'
import { applyHeadFollow, headFollowRuntime } from '../lib/headFollowRuntime.js'
import { eyeFollowRuntime } from '../lib/eyeFollowRuntime.js'
import { updateEyeFollowFromScene, resetEyeFollowScene } from '../lib/eyeFollowScene.js'
import { layeredBoneBlendRuntime } from '../lib/layeredBoneBlendRuntime.js'
import { avatarFloor } from '../lib/avatarFloor.js'
import { buildMaterialsFromAssets, applyMaterialMapping, livePreviewMaterial, tickGraphUniforms, setGraphParam, initKTX2, whenTexturesSettled, getLastBuildTextureUrls, upgradeActiveMaterialsTo } from '../lib/avatarMaterial.js'
import { initTextureQuality, getTargetTier, getActiveTier, setActiveTier, getOverridesVersion, recordFromResourceTimings, subscribe as subscribeTextureQuality } from '../lib/textureQuality.js'
import { compileMaterialGraph } from '../lib/materialGraphCompiler.js'
import { resolveActiveCharacter } from '../lib/characterState.js'
import { checkForRig } from '../lib/checkForRig.js'
import { bakeAdditive, buildRestPose, buildBoneParents } from '../lib/additiveBake.js'
import { buildGeneratedGestureClip } from '../lib/generatedGestureClip.js'
import { isTracksJsonPayload } from '../lib/bodyClipPayload.js'
import { listAnimationAssets, listMaterials, listTextureAssets, getMaterialMapping } from '../lib/assetManagerApi.js'
import { currentCollabMapping } from '../lib/collabMappingBridge.js'
import { devLog } from '../lib/devLog.js'
import { fetchGlbBytesCached } from '../lib/glbCache.js'
import { computePriorityClipNames } from '../lib/priorityClips.js'
import { clipRuntimeKeys } from '../lib/clipRuntimeKeys.js'
import { pruneBodyClipTracks } from '../lib/pruneBodyClipTracks.js'
import { createBodyClipLoader } from '../lib/BodyClipLoader.js'
import { setBodyClipLoader } from '../lib/bodyClipLoaderRef.js'

// Expose internals for e2e tests
window.__SM_RUNTIME__      = smRuntime
window.__SET_VARIABLE__    = setVariable
window.__VARIABLE_VALUES__ = variableValues
window.__DEBUG_STATE__     = debugState

// Developer log gate — same flag the HUD uses. Logs are expensive with DevTools
// open (1–3ms each), so keep them off by default and opt in via query/localStorage.
const DEBUG = (typeof window !== 'undefined')
  && (new URLSearchParams(window.location.search).get('debug') === '1'
    || localStorage.getItem('debug') === '1')

// Rig path comes from the active character config (see characters.json +
// characterState.js). Rig GLBs are Draco-compressed and contain mesh +
// skeleton + optional ARKit morphs, no animations. Built by
// `aosTools/threejs/fbx_rig_to_glb.py`.
//
// Per-clip animation GLBs live next to the rig and are enumerated by the
// manifest. Each contains a single THREE.AnimationClip whose name matches
// the manifest entry (set canonical at conversion time, see
// `aosTools/threejs/anim_fbx_to_glb.py`). Three.js binds clip tracks to the rig by
// bone name at runtime — same MetaHuman skeleton assumed across all rigs.
// Stock body clips now live in the DB+S3 (uploaded once via
// local-dev/import-stock-assets.py, served via /api/characters/{slug}/
// animation-assets). The legacy public/assets/animations bundle is being
// removed — no more static manifest fetches.

// Materials are read off the server via the material_assets system —
// see avatarMaterial.buildMaterialsFromAssets / applyMaterialMapping.
// The legacy compileMeshSlots / applyMaterialsByRole / role-aliases
// codepath was removed in Phase 7b.

/**
 * Iterate a graph's stateMachine nodes in place; when any SM's currentState
 * changed since last frame, reset the new state's clip to time=0.
 * Avoids the array spread of [...body.filter(...), ...face.filter(...)].
 */
function detectSMTransitions(nodes, actions, prevMap) {
  for (const node of nodes) {
    if (node.type !== 'stateMachine') continue
    const runtime = smRuntime[node.id]
    if (!runtime?.currentState) continue

    const prev = prevMap[node.id]
    if (runtime.currentState === prev) continue

    const innerNodes = node.data.innerNodes ?? []
    const stateNode  = innerNodes.find(n => n.id === runtime.currentState)
    // Reset time=0 for every playClip in the state's inner graph. Simpler than
    // tracking which Select/Random variant is active; resetting an idle action
    // is harmless (next play starts at 0 regardless).
    const stateInner = stateNode?.data?.innerNodes ?? []
    for (const n of stateInner) {
      if (n.type !== 'playClip') continue
      const a = actions[n.data?.clipName]
      if (a) a.time = 0
    }
    // Backward-compat: legacy blueprints still keep clipName on the state itself.
    const legacyClip = stateNode?.data?.clipName
    const legacyAction = legacyClip ? actions[legacyClip] : null
    if (legacyAction) legacyAction.time = 0
    if (DEBUG) {
      const label = legacyClip ?? (stateInner.find(n => n.type === 'playClip')?.data?.clipName ?? '(no clip)')
      console.log(`[SM] → "${runtime.currentState}" | clip: "${label}" | 0.00%`)
    }
    prevMap[node.id] = runtime.currentState
  }
}

export default function AvatarModel({ character, animationBuffer, onLoaded, onError, onStage }) {
  const { camera, gl }  = useThree()
  if (typeof window !== 'undefined') {
    window.__AVATAR_CAMERA__   = camera
    window.__AVATAR_RENDERER__ = gl
  }
  // Wire the KTX2/Basis transcoder to this renderer so material textures can
  // load their GPU-compressed variant (ENG-753). Idempotent; falls back to
  // PNG/JPG when a variant is absent or unsupported.
  initKTX2(gl)
  // Read the device's texture-quality signals (GPU cap, bandwidth prior) once
  // per session (ENG-757). Also idempotent.
  initTextureQuality(gl)
  const groupRef        = useRef(null)
  const loadingRef      = useRef(false)
  const loadStateRef    = useRef({ body: false, face: false, boneCount: 0 })
  const mixerRef        = useRef(null)
  const actionsRef      = useRef({})   // { clipName → THREE.AnimationAction }
  const prevSMStateRef  = useRef({})   // { smNodeId → stateId } — detect transitions
  const diagDoneRef     = useRef(false) // one-time diagnostic flag
  const diagIntervalRef = useRef(0)    // periodic diagnostic counter
  // Reused per-frame merge target — body weights + non-livelink face weights
  // sum into here. Allocating once and clearing each frame keeps GC pressure
  // off the render loop.
  const mergedRef       = useRef({})
  // Cached subset of graphState.nodes whose type === 'playClip'. Rebuilt
  // when the node array identity changes; the per-frame auto-restart scan
  // walks this short list (typically 1–3) instead of all 30+ nodes.
  const playClipNodesRef = useRef([])
  const playClipNodesSrcRef = useRef(null)
  // ENG-524: the auto-restart below only exists to re-roll Random nodes when a
  // non-looping clip finishes. Without a Random node it would just replay the
  // same clip — which breaks a plain Loop=off Play Clip's "hold the final frame"
  // (Unreal bLoop=false) behavior. Cached with the node list (structure change).
  const hasRandomNodeRef = useRef(false)
  const additiveActionsRef  = useRef({})   // { clipName → pre-baked additive THREE.AnimationAction }
  const additiveClipNamesRef = useRef(new Set()) // clip names flagged additive in manifest.json

  // Deformer (phase:"postMixer") plugin nodes read the FINAL posed skeleton +
  // audio state through this frameCtx. Built once; `.delta` is mutated per frame
  // (no per-frame allocation). getSkeleton caches the first SkinnedMesh's skeleton.
  const bodySkeletonRef = useRef(null)
  const deformerFrameCtxRef = useRef(null)
  if (!deformerFrameCtxRef.current) {
    deformerFrameCtxRef.current = {
      delta: 0,
      getArkit: () => getCurrentArkit(),
      // The avatar group — a deformer adds its own mesh/skeleton here to render.
      getRoot: () => groupRef.current,
      getSkeleton: () => {
        if (bodySkeletonRef.current) return bodySkeletonRef.current
        const root = groupRef.current
        if (!root) return null
        let skel = null
        root.traverse(o => { if (!skel && o.isSkinnedMesh) skel = o.skeleton })
        bodySkeletonRef.current = skel
        return skel
      },
    }
  }
  const sceneNodesRef        = useRef(null)      // Set<string> of rig bone/mesh names for track pruning
  const rigSceneRef          = useRef(null)      // loaded rig scene root — used by __RELOAD_MATERIALS__
  const graphClockRef        = useRef(0)         // accumulated time for material-graph uTime uniforms
  const textureRowsRef       = useRef([])        // latest texture_assets rows — tier re-targeting (ENG-757)

  // ENG-757: re-target texture tiers live when the quality setting (or the
  // measured-bandwidth auto decision) changes. Downgrades allowed here — a
  // manual "Low" pick must actually swap down. The guard against churn is
  // activeTier: no-op when the avatar already shows the target.
  const overridesVersionRef = useRef(getOverridesVersion())
  const retargetInFlightRef = useRef(false)
  useEffect(() => {
    const retarget = () => {
      // The upgrade pass itself notifies (progress ticks) — never re-enter.
      // A change landing mid-flight is caught by the trailing re-check below.
      if (retargetInFlightRef.current) return
      const rows = textureRowsRef.current
      if (!rows.length) return
      const target = getTargetTier()
      const ovVersion = getOverridesVersion()
      // Re-target when the global tier changed OR a per-texture pin changed
      // (a pin can move one texture without moving the global tier).
      if (target === getActiveTier() && ovVersion === overridesVersionRef.current) return
      overridesVersionRef.current = ovVersion
      retargetInFlightRef.current = true
      upgradeActiveMaterialsTo(target, rows, { allowDowngrade: true })
        .then((n) => {
          setActiveTier(target)
          if (n) console.log(`[AvatarModel] texture tier re-target → ${target} (${n} slots)`)
        })
        .catch((e) => console.warn('[AvatarModel] texture tier re-target failed:', e))
        .finally(() => {
          retargetInFlightRef.current = false
          retarget() // pick up anything that changed while we were swapping
        })
    }
    return subscribeTextureQuality(retarget)
  }, [])

  useFrame((_, delta) => {
    // Body + Face: evaluate BOTH graphs in a single pass so they see
    // identical variable state and transition on the same frame.
    if (mixerRef.current) {
      // 1. SM transitions — body then face, both read the same variableValues
      tracePhase('smTransitions', () => {
        updateSMTransitions(graphState)
        updateSMTransitions(faceGraphState)
      })

      // 2. Detect state transitions in BOTH graphs — reset target clip to time=0.
      //    Iterate both node lists in place rather than spreading into a temp array.
      tracePhase('detectSM', () => {
        detectSMTransitions(graphState.nodes, actionsRef.current, prevSMStateRef.current)
        detectSMTransitions(faceGraphState.nodes, actionsRef.current, prevSMStateRef.current)
      })

      // 3. Evaluate both graphs
      // Reset LBB config once here — body graph sets it if an LBB node is
      // active; face graph must not clobber it (both call evaluateGraph).
      layeredBoneBlendRuntime.setConfig(null)
      let bodyWeights, bodyLoop, bodyAdditive, bodySpeed, faceWeights, faceSpeed
      tracePhase('evaluateGraphs', () => {
        if (!window.__UPLOAD_ANIM_MODE__) {
          ({ weights: bodyWeights, loopSettings: bodyLoop, additiveSettings: bodyAdditive, speedSettings: bodySpeed } = evaluateGraph(graphState));
          ({ weights: faceWeights, speedSettings: faceSpeed } = evaluateGraph(faceGraphState))
        }
      })

      // One-time diagnostic: log body graph state on first evaluation
      if (DEBUG && !diagDoneRef.current) {
        diagDoneRef.current = true
        const smNode = graphState.nodes.find(n => n.type === 'stateMachine')
        console.log(`[AvatarModel] Body graph diagnostic:`,
          `nodes=${graphState.nodes.length}`,
          `edges=${graphState.edges.length}`,
          `smNode=${smNode?.id ?? 'NONE'}`,
          `innerNodes=${smNode?.data?.innerNodes?.length ?? 0}`,
          `innerEdges=${smNode?.data?.innerEdges?.length ?? 0}`,
          `bodyWeights=`, bodyWeights)
      }

      // Periodic diagnostic: warn if body weights are empty (every ~5s)
      if (DEBUG && ++diagIntervalRef.current % 300 === 0 && !bodyWeights) {
        const smNode = graphState.nodes.find(n => n.type === 'stateMachine')
        console.warn(`[AvatarModel] Body weights NULL — graph may be empty.`,
          `nodes=${graphState.nodes.length} edges=${graphState.edges.length}`,
          `smNode=${smNode?.id ?? 'NONE'}`,
          `innerNodes=${smNode?.data?.innerNodes?.length ?? 0}`)
      }

      // 4. Merge body + face clip weights into the long-lived `merged` object.
      // Only merge face clips that exist in the mixer — face clips need
      // ARKit-format export (Source/anm/*_face_cra.fbx are MetaHuman
      // control-rig curves that can't bind to the ARKit blendshape rig).
      const merged = mergedRef.current
      // Drop stale keys + copy bodyWeights, all in place — no allocation.
      for (const k in merged) delete merged[k]
      if (bodyWeights) for (const k in bodyWeights) merged[k] = bodyWeights[k]
      if (faceWeights) {
        const actions = actionsRef.current
        for (const clipName in faceWeights) {
          if (clipName === '__livelink__' || clipName === '__headfollow__' || clipName === '__eyefollow__') continue
          if (actions[clipName]) {
            merged[clipName] = (merged[clipName] ?? 0) + faceWeights[clipName]
          }
        }
      }

      // 5. Apply merged weights + advance mixer
      debugState.clipWeights = merged
      debugState.bodyWeights = bodyWeights ?? {}
      debugState.faceWeights = faceWeights ?? {}
      const mergedSpeed = { ...(bodySpeed ?? {}), ...(faceSpeed ?? {}) }
      tracePhase('applyWeights', () => applyWeights(merged, bodyLoop, actionsRef.current, bodyAdditive, additiveActionsRef.current, additiveClipNamesRef.current, mergedSpeed))
      layeredBoneBlendRuntime.preUpdate(mixerRef.current, actionsRef.current)
      // Clamp the frame delta ONLY while a followAction hold (a generated
      // one-shot) is active. After a stalled frame (tab hidden, or the GPU
      // saturated by same-device work like kimodo generation) R3F hands us the
      // full elapsed wall time; without a clamp that fast-forwards the freshly
      // started clip past most of its motion. Scoping the clamp to followAction
      // avoids two regressions: legacy wall-clock one-shots (prefabs/gestures)
      // whose expiry tracks real time would get cut short by a slower-than-
      // realtime mixer, and the post-mixer overrides below would otherwise
      // integrate a delta the mixer no longer sees. Same clamped `fdelta` feeds
      // the mixer AND both post-mixer overrides so they stay in lockstep.
      const fdelta = (typeof window !== 'undefined' && window.__DIRECT_PLAY__?.followAction)
        ? Math.min(delta, 0.1)
        : delta
      tracePhase('mixer.update', () => mixerRef.current.update(fdelta))
      // Root motion of an extracted generated clip. AFTER the update, because it
      // reads the action's time and the mixer is what advances it — placing the
      // group from the pre-update time left it a frame behind the pose, by an
      // amount that varied with the frame time and therefore jittered.
      tracePhase('rootDrive', () => applyRootDrive(actionsRef.current))
      tracePhase('layeredBoneBlend', () => layeredBoneBlendRuntime.postUpdate(groupRef.current, mixerRef.current, actionsRef.current))

      // Post-mixer: rotate neck chain toward camera at the sentinel weight.
      // MUST run after mixer.update — otherwise the clip pose overwrites the
      // slerped look-at rotation on the same frame.
      tracePhase('headFollow', () => {
        const hfWeight = faceWeights?.['__headfollow__'] ?? 0
        // neckShare() rides the SMOOTHED gate, so it is 0 for a graph with no
        // eyeFollow node and fades on its own when one is switched off. It reads
        // LAST frame's weight because Eye Follow now advances below — one frame
        // of lag on a smoothed 0..1 gate, invisible, and the alternative is the
        // eyes reading a head that has not turned yet (see next block).
        applyHeadFollow(groupRef.current, camera, Math.min(1, hfWeight + eyeFollowRuntime.neckShare()), fdelta)
      })

      // Eye Follow advances AFTER head-follow, and the order is the whole fix
      // (ENG-1060). The eyes aim relative to the HEAD's current forward, and
      // `mixer.update()` above resets that bone to its CLIP pose every frame —
      // applyHeadFollow is what turns it toward the camera. Running the eyes
      // first therefore read a forward-facing head and handed them the FULL
      // angle on top of a neck that had already turned: measured on myra, the
      // camera 45.4° off body-forward, the head at 43.1°, and the eyes driven
      // 35° instead of the 2.3° that remained — so they shot past the camera.
      // Ticked every frame regardless of the gate — a node switched off must
      // fade its aim out, not freeze at the last value it held.
      tracePhase('eyeFollow', () => {
        updateEyeFollowFromScene(groupRef.current, camera, faceWeights?.['__eyefollow__'] ?? 0, fdelta)
      })

      // Post-mixer: run deformer (phase:"postMixer") plugin nodes now that the
      // skeleton holds its final posed transforms. They drive their own meshes
      // (GPU morph / vertex upload) and contribute no clip weights.
      if (graphState.nodes.length) {
        // Same clamped fdelta as the mixer + other post-mixer passes (see the
        // clamp rationale above) so cloth integration stays in lockstep.
        deformerFrameCtxRef.current.delta = fdelta
        tracePhase('deformerPostMixer', () => evaluatePostMixer(graphState, deformerFrameCtxRef.current))
      }

      // 6. Auto-restart: when a non-looping body clip finishes, re-roll random
      //    nodes so the next evaluate picks a fresh selection.
      //
      //    Gated on the graph actually having a Random node (ENG-524): the only
      //    point of re-rolling + reset() is to vary a Random-driven selection.
      //    With no Random node, resetting a finished non-looping clip would just
      //    replay it — defeating Loop=off's "play once, hold the final frame"
      //    (Unreal bLoop=false). So a plain Play Clip with Loop off now holds,
      //    while Random-driven idle variety still cycles as before.
      //
      //    The cached playClip list (+ hasRandom flag) is rebuilt only when
      //    graphState.nodes identity changes (graph load / structural edit), so
      //    the per-frame work scales with the number of playClip nodes —
      //    typically 1–3 — not the full graph (~30 nodes).
      if (playClipNodesSrcRef.current !== graphState.nodes) {
        playClipNodesSrcRef.current = graphState.nodes
        const out = []
        let hasRandom = false
        for (const n of graphState.nodes) {
          if (n.type === 'playClip') out.push(n)
          else if (n.type === 'random') hasRandom = true
        }
        playClipNodesRef.current = out
        hasRandomNodeRef.current = hasRandom
      }
      if (hasRandomNodeRef.current) {
        for (const node of playClipNodesRef.current) {
          const clipName = node.data?.clipName
          if (!clipName) continue
          if (bodyLoop?.[clipName] !== false) continue   // skip looping clips
          const action = actionsRef.current[clipName]
          if (!action) continue
          const weight = bodyWeights?.[clipName] ?? 0
          if (weight > 0 && !action.isRunning() && action.time >= action.getClip().duration - 0.02) {
            clearStickyRandomsInNodes(graphState.nodes)
            action.reset()
          }
        }
      }

      // 7. Write clip timings for ruleAnimTimeRemaining / ruleAnimTimeRatio.
      //    Only active clips — skips the O(n) per-frame churn over the full library.
      //    Mutate existing timing objects in place to avoid per-frame allocations.
      if (bodyWeights) {
        for (const clipName in bodyWeights) {
          if (bodyWeights[clipName] <= 0) continue
          const action = actionsRef.current[clipName]
          if (!action) continue
          const clip      = action.getClip()
          const time      = action.time % clip.duration
          const remaining = clip.duration - time
          const ratio     = time / clip.duration
          const existing  = debugState.clipTimings[clipName]
          if (existing) {
            existing.time      = time
            existing.duration  = clip.duration
            existing.remaining = remaining
            existing.ratio     = ratio
          } else {
            debugState.clipTimings[clipName] = { time, duration: clip.duration, remaining, ratio }
          }
        }
      }

      // 7. Export face eval result for HeadModel to consume
      const liveWeight = window.__UPLOAD_ANIM_MODE__ ? 0 : (faceWeights?.['__livelink__'] ?? 0)
      faceEvalResult.liveWeight = liveWeight
      faceEvalResult.weights    = window.__UPLOAD_ANIM_MODE__ ? null : (faceWeights ?? null)
      debugState.clipWeights['__livelink__']   = liveWeight
      debugState.clipWeights['__headfollow__'] = faceWeights?.['__headfollow__'] ?? 0
    }

    graphClockRef.current += delta
    tickGraphUniforms(graphClockRef.current)
  })

  useEffect(() => {
    // Re-entrancy latch for ONE mount, RELEASED BY THE CLEANUP BELOW. Both
    // halves are load-bearing and the missing release was a shipped bug: React
    // StrictMode runs a fresh mount's effect, tears it down, then runs it again,
    // so a latch that is never released blocks the SECOND (live) run and leaves
    // the FIRST — the one the cleanup just marked `cancelled` — as the only
    // loader in flight. It walks the whole pipeline, reaches the `cancelled`
    // gate below, and returns without publishing a rig, clearing the stage, or
    // reporting an error: the character switch hangs forever on whatever label
    // it happened to reach ("Listing animation assets…"), with nothing in the
    // console. Deterministic on every in-place character switch — a first mount
    // gets a single effect run and so never showed it.
    if (loadingRef.current) return
    loadingRef.current = true

    // This instance is gone once the cleanup below runs, but `loadAll` is async
    // and keeps going — so the publish points check this before pointing a
    // global at a rig nobody is rendering any more. Without it, an unmount
    // mid-load RESURRECTS the globals the cleanup just cleared.
    let cancelled = false

    window._diagnosticMorphs = diagnosticMorphTargets

    const group = groupRef.current
    if (!group) return

    // Shared GLTFLoader with Draco support (rig GLB is Draco-compressed).
    const dracoLoader = new DRACOLoader().setDecoderPath('/draco/')
    const gltfLoader  = new GLTFLoader().setDRACOLoader(dracoLoader)
    const loadGlb = (path) => new Promise((resolve, reject) => {
      gltfLoader.load(path, resolve, undefined, reject)
    })
    const parseGlb = (arrayBuffer) => new Promise((resolve, reject) => {
      gltfLoader.parse(arrayBuffer, '', resolve, reject)
    })
    // Parse either a GLB or a kimodo tracks-JSON payload (isTracksJsonPayload)
    // into the shape loadAndRegister expects. Tracks JSON comes back with
    // `animations: []` and `tracksPayload` set — the caller builds the additive
    // in-place clip itself (buildGeneratedGestureClip, same conversion the live
    // WS gesture path uses) since it needs the entry name and rest-pose context.
    const parseGlbOrTracks = (buf) => {
      if (isTracksJsonPayload(buf)) {
        return Promise.resolve({ animations: [], tracksPayload: JSON.parse(new TextDecoder().decode(buf)) })
      }
      return parseGlb(buf)
    }
    // Cache-first GLB (or kimodo tracks-JSON) load. fetchGlbBytesCached persists
    // bytes in the animCache (Cache Storage) keyed by URL + version so reloads
    // skip the download, with the loud empty/HTML guards. blob: URLs (local
    // preview) and versionless URLs bypass the cache and load straight from the
    // URL. A leading '{' routes the returned bytes to the tracks-JSON branch;
    // the HTML ('<') guard fires inside fetchGlbBytesCached before we get here.
    const loadGlbCached = async (url, version) => {
      if (!url || url.startsWith('blob:') || version == null) return loadGlb(url)
      return await parseGlbOrTracks(await fetchGlbBytesCached(url, version))
    }

    async function loadAll() {
      // 1. Rig GLB — provides the scene + skeleton + ARKit morph targets.
      //
      // Resolve the FULL character config from /assets/characters.json so
      // we get the per-role `materials` + `meshSlots` fields. The synchronous
      // BUILTIN registry only carries the minimal boot shape, which doesn't
      // include those keys. The async hop is fine here — AvatarModel doesn't
      // signal __AVATAR_READY__ until the rig + clips load anyway.
      // Loud failure: if /api/characters can't be reached, surface the error
      // up the stack instead of falling back to the stale boot character.
      // The App error boundary renders a visible overlay. See docs/UX_PRINCIPLES.md.
      const fullCharacter = await resolveActiveCharacter()
      // Prefer the prop's rigPath: it may be a blob URL from local preview in CharacterAuthoringPanel.
      // resolveActiveCharacter() may return a stale server URL from characters.json.
      // Normalize raw S3 character-asset URLs (returned by the rig-upload step of
      // Sync-to-Cloud) to the API proxy — the S3 bucket 403s on direct fetch, which
      // produced the "Couldn't load this character" screen after a sync.
      // Defensive guard — App.jsx gates this mount on checkForRig and shows the rig-missing overlay instead.
      if (!checkForRig(character) && !checkForRig(fullCharacter)) {
        const err = new Error(
          `Character "${fullCharacter?.id ?? 'unknown'}" has no rig uploaded yet, so there's nothing to display. ` +
          `Upload a rig GLB or OGS assets in the content browser, or switch to another character.`)
        err.code = 'NO_RIG'
        err.characterId = fullCharacter?.id
        throw err
      }
      const rigPath = toProxiedAssetUrl(character?.rigPath || fullCharacter?.rigPath)
      if (!/^https?:\/\//i.test(rigPath) && !rigPath.startsWith('blob:')) {
        console.warn(`[AvatarModel] character ${fullCharacter?.id} rigPath is not absolute: ${rigPath} — should be an https:// URL from S3`)
      }
      console.log(`[AvatarModel] Loading character=${fullCharacter?.id} rig=${rigPath}`)
      // Drive the UI loading indicator (onStage) always, but keep the console
      // focused: skip the per-clip "Loading animations N / 32…" spam (32 lines);
      // milestone stages still log. Full detail with ?debug=1.
      const verboseLog = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('debug') === '1'
      const stage = (label) => { onStage?.(label); if (verboseLog || !/^Loading animations /.test(label)) devLog(`[AvatarModel] stage: ${label}`) }
      // The unmounted-mid-load bail. Publishing globals for a rig nobody renders
      // is the thing being prevented, so the RETURN is correct — the SILENCE was
      // not: this exit leaves the loading overlay on its last stage label with no
      // error and no console line, which is indistinguishable from a hang (it WAS
      // the hang, while the latch above was never released). It says so now, and
      // deliberately does NOT touch `stage`: a successor run owns the label by
      // then, and clearing it here would blank a live load's status instead.
      const abandoned = (where) => {
        if (!cancelled) return false
        devLog(`[AvatarModel] load abandoned at ${where} — this instance unmounted mid-load (character=${fullCharacter?.id})`)
        return true
      }
      stage('Loading rig GLB…')

      // Log texture URLs per role so we can see if they're data URLs, S3 URLs, or missing
      const matCfg = fullCharacter?.materials ?? {}
      const texLog = Object.entries(matCfg).map(([role, cfg]) => {
        if (!cfg || typeof cfg !== 'object') return null
        const slots = ['diffuse', 'normal', 'roughness'].filter(k => cfg[k])
        if (!slots.length) return null
        const preview = (url) => url.startsWith('data:') ? `data:…(${url.length}b)` : url
        return `${role}[${slots.map(k => `${k}=${preview(cfg[k])}`).join(' ')}]`
      }).filter(Boolean)
      if (texLog.length) console.log('[AvatarModel] textures:', texLog.join(' | '))
      else console.warn('[AvatarModel] no texture URLs in character config — materials will use base colors only')

      const rigGltf = await loadGlbCached(rigPath, fullCharacter?.rigUpdatedAt ?? fullCharacter?.updatedAt ?? null)
      const combined = rigGltf.scene
      combined.name = '__body__'
      rigSceneRef.current = combined

      // NOTE: the polygonal hair is NOT stripped here. Hiding it is owned by
      // HairSplat (it sets the hair mesh visible=false on attach and restores
      // it on unmount) so the "Hair" dropdown can switch None↔asset live
      // without a reload. Destroying the mesh here would make "None" unable to
      // restore the polygonal hair without a full reload.

      // Materials come from material_assets + material_mapping. Brand-new
      // characters get them via the glb_import server flow on rig upload;
      // pre-existing characters via POST /migrate-materials. A character
      // with an empty mapping renders with no material assignments (the
      // GLB's own materials, if any, otherwise gray defaults) — surface
      // this loudly rather than silently fall back.
      const slugForMaterials = fullCharacter?.id
      if (!slugForMaterials) {
        throw new Error('[AvatarModel] character has no slug — cannot fetch material assets')
      }
      stage('Loading materials + textures…')
      const [materialRows, textureRows, mapping] = await Promise.all([
        listMaterials(slugForMaterials).catch(() => []),
        listTextureAssets(slugForMaterials).catch(() => []),
        getMaterialMapping(slugForMaterials).catch(() => []),
      ])
      if (mapping.length === 0 || materialRows.length === 0) {
        console.warn(`[AvatarModel] character "${slugForMaterials}" has no material_mapping yet — run POST /api/characters/${slugForMaterials}/migrate-materials or re-upload the rig GLB to auto-extract.`)
      } else {
        // ENG-757 cascade: when the server has a KTX2 size ladder for this
        // character, pass 1 builds at the low tier (tiny files → avatar visible
        // fast) and pass 2 upgrades in the background to the target tier once
        // the real download throughput is known. Without a ladder this is
        // exactly the ENG-753 single-pass load.
        textureRowsRef.current = textureRows
        const hasLadder = textureRows.some(t => t.ktx2_variants && typeof t.ktx2_variants === 'object' && Object.keys(t.ktx2_variants).length > 0)
        const passOneTier = hasLadder ? 'low' : getTargetTier()
        const built = buildMaterialsFromAssets({ materials: materialRows, textures: textureRows, mapping, tier: passOneTier })
        applyMaterialMapping(combined, built)
        console.log(`[AvatarModel] applied ${materialRows.length} material assets across ${mapping.length} mesh slots (tier: ${passOneTier})`)
        void (async () => {
          try {
            await whenTexturesSettled()
            recordFromResourceTimings(getLastBuildTextureUrls())
            const target = getTargetTier() // re-read: the measurement may move the auto pick
            if (hasLadder && target !== passOneTier) {
              const n = await upgradeActiveMaterialsTo(target, textureRows)
              console.log(`[AvatarModel] texture tier cascade: ${passOneTier} → ${target} (${n} slots)`)
            }
            setActiveTier(target)
          } catch (e) {
            console.warn('[AvatarModel] texture tier cascade failed:', e)
          }
        })()
      }

      let boneCount = 0
      combined.traverse((n) => { if (n.isBone) boneCount++ })

      const mixer = new THREE.AnimationMixer(combined)
      mixerRef.current = mixer

      // Notify head-follow when any body clip wraps (loops). The mixer fires
      // 'loop' for every action that completes a cycle; we forward that as a
      // one-frame "skip chain rotation" signal so the discrete clip-end-to-start
      // discontinuity in the spine doesn't translate (via the share-distributed
      // chain rotation) into a visible head-position swing.
      mixer.addEventListener('loop', () => headFollowRuntime.notifyClipLoop())

      // NOTE: don't add `combined` to the R3F group yet. We flip
      // `window.__AVATAR_READY__` once actions are registered below;
      // adding the rig early causes tests (and any consumer waiting on
      // __AVATAR_READY__) to read __AVATAR_ACTIONS__ / __CLIP_BINDINGS__
      // before the anim GLBs finish loading. The scene is added after
      // actions are registered below.

      // 2. Manifest → per-clip GLBs. Each anim GLB contains exactly one
      //    AnimationClip whose name matches the manifest entry. Three.js
      //    binds tracks by bone name at runtime, so clips authored against
      //    the same MetaHuman skeleton play correctly on the rig.
      // Pull body clips from the DB+S3 via the per-character animation-assets
      // endpoint. Each row carries { name, kind, additive, file_url } — same
      // contract as the legacy manifest, just sourced from the server of
      // truth instead of a shipped JSON.
      const slug = fullCharacter?.id
      if (!slug) {
        throw new Error('No character slug — cannot fetch animation assets')
      }
      stage('Listing animation assets…')
      const allAssets = await listAnimationAssets(slug)
      const bodyAssets = (allAssets ?? []).filter(a => a.kind === 'body')
      // assetId → clip name, so an additive clip's base_pose_asset_id (ENG-442)
      // resolves to the loaded clip used as its reference pose.
      const idToName = new Map(bodyAssets.map(a => [a.id, a.name]))
      const bodyEntries = bodyAssets.map(a => ({
        name: a.name,
        additive: !!a.additive,
        url: a.file_url,
        // Cache-bust token for animCache — changes when the clip is re-uploaded.
        version: a.updatedAt ?? a.updated_at ?? a.id ?? null,
        // ENG-442 Unreal-parity additive settings (server defaults: none/none/0).
        additiveType: a.additiveType ?? null,
        basePoseType: a.basePoseType ?? null,
        basePoseClipName: a.basePoseAssetId ? (idToName.get(a.basePoseAssetId) ?? null) : null,
        refFrameIndex: a.refFrameIndex ?? 0,
        // Extra runtime keys this asset also resolves by — the
        // `custom_body_<id>` alias (clipRuntimeKeys.js). Registered HERE, by the
        // clip's owner, as the clip lands: an alias is the same bytes under a
        // second name, so it must never justify a download. CustomAssetManager
        // used to build them by re-fetching and re-parsing every GLB after
        // ready, which raced this loader for the same files.
        aliases: clipRuntimeKeys(a).filter(k => k !== a.name),
      }))
      const bodyClipNames = bodyEntries.map(e => e.name)
      const additiveClipNames = new Set(bodyEntries.filter(e => e.additive).map(e => e.name))
      additiveClipNamesRef.current = additiveClipNames
      window.__ADDITIVE_CLIP_NAMES__ = additiveClipNames
      // Resolved per-clip additive settings, keyed by clip name, for the bake below.
      const additiveSettingsByName = new Map(
        bodyEntries.map(e => [e.name, {
          additiveType: e.additiveType,
          basePoseType: e.basePoseType,
          basePoseClipName: e.basePoseClipName,
          refFrameIndex: e.refFrameIndex,
        }]),
      )
      // ── Per-clip registration plumbing ──
      // Shared by the priority load below, the background BodyClipLoader, and
      // __REGISTER_CUSTOM_BODY_CLIP__. Source clips are authored against the full
      // MetaHuman skeleton (corrective/twist bones the rig lacks); each clip is
      // pruned, optionally baked additive (ENG-442 Unreal-parity settings), and
      // registered on the live mixer one at a time so the avatar can become ready
      // after only a few clips and stream the rest in the background.
      const sceneNodes = new Set()
      combined.traverse(n => sceneNodes.add(n.name))
      sceneNodesRef.current = sceneNodes

      // makeClipAdditive's reference resolution (anim_frame/anim_scaled base
      // poses) reads other clips out of clipsByName — we populate it incrementally
      // as clips register, and loadAndRegister() pulls in any external base pose
      // clip first (below).
      const bakeCtx = {
        clipsByName: new Map(),
        restPose: buildRestPose(combined),
        boneParents: buildBoneParents(combined),
        fps: 30,
      }
      const entryByName = new Map(bodyEntries.map(e => [e.name, e]))
      const clipBindings = {}
      const actions = actionsRef.current        // stable container; fill in place
      const additiveActionMap = additiveActionsRef.current

      // Point the test/debug globals at the live containers BEFORE loading so
      // clips become visible to consumers as they register (JIT: actions fill
      // incrementally instead of all-at-once).
      if (abandoned('debug-global publish')) return
      window.__AVATAR_MIXER__            = mixer
      window.__AVATAR_ACTIONS__          = actions
      window.__AVATAR_ADDITIVE_ACTIONS__ = additiveActionMap
      window.__AVATAR_SCENE__            = combined
      window.__CLIP_BINDINGS__           = clipBindings

      // Register one already-parsed AnimationClip on the mixer. Idempotent — a
      // clip pulled in as another clip's base pose (or re-reached by the
      // background loader) won't double-register unless `replace` is set.
      // `additiveInPlace`: the clip's tracks are ALREADY additive deltas baked
      // against the rig rest pose (generated kimodo gestures) — register it as a
      // member of additiveClipNames WITHOUT running bakeAdditive again, so it is
      // treated exactly like a stock manifest-additive gesture_* clip:
      // ensureAdditiveClone returns null (no clone / no second makeClipAdditive),
      // and GraphEvaluator.applyWeights drives it in AdditiveAnimationBlendMode.
      const registerBodyClip = (clipName, clip, { additive = false, additiveSettings = null, replace = false, generatedMarker = null, additiveInPlace = false } = {}) => {
        if (actions[clipName] && !replace) return clipBindings[clipName]
        clip.name = clipName
        // Discoverable as a base pose for other additive clips.
        bakeCtx.clipsByName.set(clipName, clip)
        // Bake additive deltas BEFORE pruning (matches the original order).
        if (additive) bakeAdditive(clip, additiveSettings, bakeCtx)

        // Prune unbindable / axis-normalization tracks → bindings report (shape
        // matches __CLIP_BINDINGS__; the pelvis .position keep/drop gate lives in
        // pruneBodyClipTracks, unit tested).
        const bindings = pruneBodyClipTracks(clip, sceneNodes, { generatedMarker })
        clipBindings[clipName] = bindings

        // Loud static-clip warning (a real authoring/export problem).
        if (bindings.bound === 0 || bindings.active === 0) {
          let hint
          if (bindings.tracks === 0) hint = 'no tracks — source FBX has no keyframes or export stripped them'
          else if (bindings.bound === 0) hint = `all ${bindings.tracks} tracks target bones missing from the rig — samples: ${bindings.samples.join(', ')}`
          else hint = `${bindings.bound} tracks bound but only ${bindings.active} have motion`
          console.warn(`[AvatarModel] clip "${clipName}" will likely look STATIC: ${hint}`)
        }

        // Register the normal action. Skip play() — actions start on demand from
        // applyWeights() based on graph output, so idle clips never burn cycles.
        actions[clipName] = mixer.clipAction(clip)

        if (additive || additiveInPlace) {
          additiveClipNamesRef.current.add(clipName)
          window.__ADDITIVE_CLIP_NAMES__?.add(clipName)
        }
        // NOTE: the B-slot additive clone (clip.clone() + makeClipAdditive) is
        // NOT made here. Cloning + baking ~290 tracks per clip is the dominant
        // synchronous cost; doing it eagerly for every clip janks the render loop
        // while clips stream in. It's created lazily on first additive use via
        // ensureAdditiveClone() below — most clips are never used additively.
        return bindings
      }

      // Register an ALREADY-REGISTERED clip under a SECOND key — no download, no
      // GLB parse, no track prune. The `custom_body_<assetId>` alias every
      // animation asset also resolves by (clipRuntimeKeys.js) names the same
      // bytes as the asset's own name, so re-fetching and re-parsing the GLB to
      // produce it was pure waste: measured on myra (58 body assets), that was
      // 58 uncached downloads plus a SECOND ~1026-track parse+prune each, all on
      // the main thread, racing this loader for the very same files.
      //
      // The clip is CLONED rather than shared. Two keys pointing at one action
      // object looks tempting and is a bug: applyWeights' __DIRECT_PLAY__ pass
      // stops every action whose key !== dp.clipName, so playing the alias would
      // find the same object under the name key and stop the very action it was
      // about to play. `mixer.clipAction` also caches per clip object, so a
      // distinct action REQUIRES a distinct clip. A clone is array copies — far
      // below a parse — and it happens once per clip as the clip lands, not per
      // frame and not in a burst.
      const aliasBodyClip = (alias, sourceName) => {
        if (!alias || alias === sourceName) return false
        if (actions[alias]) return true              // idempotent
        const src = actions[sourceName]
        if (!src) return false                       // source not registered (yet)
        const clip = src.getClip().clone()
        clip.name = alias
        actions[alias] = mixer.clipAction(clip)
        // Carry the source's bindings report and additive membership across: the
        // alias IS the same animation, so a consumer reading either through the
        // alias must not get a different answer than through the name.
        clipBindings[alias] = clipBindings[sourceName]
        if (additiveClipNamesRef.current.has(sourceName)) {
          additiveClipNamesRef.current.add(alias)
          window.__ADDITIVE_CLIP_NAMES__?.add(alias)
        }
        return true
      }

      // Give a just-registered entry its extra runtime keys (the
      // `custom_body_<id>` alias). Called from loadAndRegister, so an alias
      // appears with its clip wherever the clip came from — the priority pass,
      // the background sweep, or an on-demand pull — and nothing downstream has
      // to poll for it or fetch the GLB again to make one.
      const aliasEntryKeys = (entry) => {
        for (const alias of entry.aliases ?? []) aliasBodyClip(alias, entry.name)
      }

      // Lazily build the additive clone an additive Blend node's B slot needs.
      // Called from GraphEvaluator.applyWeights the first frame a clip is driven
      // additively; cached in additiveActionMap thereafter. Returns null if the
      // base action isn't registered yet (the caller falls back to the normal
      // action that frame).
      const ensureAdditiveClone = (clipName) => {
        if (additiveActionMap[clipName]) return additiveActionMap[clipName]
        if (additiveClipNamesRef.current.has(clipName)) return null // baked in place; no clone
        const base = actions[clipName]
        if (!base) return null
        const clone = base.getClip().clone()
        THREE.AnimationUtils.makeClipAdditive(clone)
        const additiveAction = mixer.clipAction(clone)
        additiveAction.play()
        additiveAction.setEffectiveWeight(0)
        additiveActionMap[clipName] = additiveAction
        return additiveAction
      }
      window.__ENSURE_ADDITIVE_CLONE__ = ensureAdditiveClone

      // Load one clip entry's GLB (cache-first) and register it. Ensures any
      // external anim_frame/anim_scaled base-pose clip is registered first so
      // bakeAdditive can resolve it. Idempotent via registerBodyClip's guard.
      const loadAndRegister = async (entry) => {
        if (actions[entry.name]) return
        const settings = additiveSettingsByName.get(entry.name)
        const baseName = settings?.basePoseClipName
        if (entry.additive && baseName && baseName !== entry.name && !bakeCtx.clipsByName.has(baseName)) {
          const baseEntry = entryByName.get(baseName)
          if (baseEntry) await loadAndRegister(baseEntry)
        }
        const gltf = await loadGlbCached(entry.url, entry.version)
        if (gltf.tracksPayload) {
          // Kimodo-generated gesture, persisted via register_gestures.py: the same
          // additive/in-place bake the live WS path uses (generatedGestureClip.js),
          // just sourced from a stored asset instead of a fresh /generate call.
          // additive:false — the clip is already baked, unlike a stock GLB entry.
          const clip = buildGeneratedGestureClip(gltf.tracksPayload, {
            scene: combined, name: entry.name, restPose: bakeCtx.restPose,
          })
          registerBodyClip(entry.name, clip, { additive: false, additiveInPlace: true })
          aliasEntryKeys(entry)
          return
        }
        // Provenance in scene extras gates the pelvis .position (travel) prune;
        // absent marker → unchanged prune (see pruneBodyClipTracks).
        const generatedMarker = gltf.scene?.userData?.aosGeneratedMotion ?? null
        for (const clip of gltf.animations) {
          registerBodyClip(entry.name, clip, { additive: entry.additive, additiveSettings: settings, generatedMarker })
        }
        aliasEntryKeys(entry)
      }

      // ── Priority clips: load + register BEFORE marking the avatar ready, so
      //    the first frame shows a real pose (idle/breathing/fidget) instead of
      //    a T-pose. Everything else streams in the background (after ready). ──
      const priorityNames = computePriorityClipNames(graphState.nodes, bodyClipNames, { cap: 8 })
      const prioritySet = new Set(priorityNames)
      const priorityEntries = bodyEntries.filter(e => prioritySet.has(e.name))
      const restEntries     = bodyEntries.filter(e => !prioritySet.has(e.name))

      stage(`Loading ${priorityEntries.length} priority animations…`)
      await Promise.all(priorityEntries.map(e =>
        loadAndRegister(e).catch(err =>
          console.warn(`[AvatarModel] priority anim load failed for "${e.name}":`, err?.message || err))))

      // Runtime hook for custom uploaded clips (CustomAssetManager). Parses a
      // GLB from ArrayBuffer and delegates to the same registration path as the
      // stock clips (prune + optional additive bake + register + B-slot clone).
      // `replace: true` so re-uploading the same name overwrites the prior action.
      //
      // CustomAssetManager also redundantly preloads every gesture/prefab-
      // referenced body clip through this exact seam (in case AvatarModel's own
      // loader hasn't reached it yet) — including a kimodo-generated gesture
      // stored as tracks JSON (see loadAndRegister's isTracksJsonPayload branch
      // above). Without this check that redundant load always threw ("THREE.
      // GLTFLoader: Unsupported asset") since the bytes aren't a GLB; harmless
      // (caught upstream, never reached registerBodyClip) but noisy on every
      // such gesture. Same detection + build as the primary loader.
      window.__REGISTER_CUSTOM_BODY_CLIP__ = async (clipName, arrayBuffer, { additive = true } = {}) => {
        devLog(`[UploadAnim] ▶ register "${clipName}" — additive=${additive}`)
        if (isTracksJsonPayload(arrayBuffer)) {
          const tracksPayload = JSON.parse(new TextDecoder().decode(arrayBuffer))
          const clip = buildGeneratedGestureClip(tracksPayload, { scene: combined, name: clipName, restPose: bakeCtx.restPose })
          const bindings = registerBodyClip(clipName, clip, { additive: false, replace: true, additiveInPlace: true })
          devLog(`[UploadAnim] ✓ registered "${clipName}" (kimodo tracks) — bound=${bindings?.bound ?? '?'}, active=${bindings?.active ?? '?'}`)
          return
        }
        const loader = new GLTFLoader()
        const gltf = await new Promise((resolve, reject) => loader.parse(arrayBuffer, '', resolve, reject))
        const generatedMarker = gltf.scene?.userData?.aosGeneratedMotion ?? null
        for (const clip of gltf.animations) {
          const preTracks = clip.tracks.length
          const bindings = registerBodyClip(clipName, clip, { additive, replace: true, generatedMarker })
          devLog(`[UploadAnim] ✓ registered "${clipName}" — additive=${additive}, tracks pre=${preTracks}→${clip.tracks.length} (bound=${bindings?.bound ?? '?'}, active=${bindings?.active ?? '?'})`)
        }
      }

      // Register an ALREADY-built AnimationClip (e.g. a kimodo-generated gesture,
      // baked additive against the rest pose in generatedGestureClip.js) directly
      // on the mixer — the tracks-object path, no GLB parse. additive:false so the
      // canonical bake doesn't run twice (the clip is already additive deltas);
      // GestureChannel forces the additive blend mode at play time and applyWeights
      // protects the gesture action by name while the channel is active.
      // These clips register as additive-in-place (additiveInPlace:true): the
      // double-bake trap is now closed in code, not just by comment. Membership in
      // additiveClipNames makes ensureAdditiveClone return null (no clone / no
      // second makeClipAdditive) and makes GraphEvaluator.applyWeights drive the
      // in-place action in AdditiveAnimationBlendMode — symmetric with stock
      // manifest-additive gesture_* clips, and correct for already-baked deltas.
      // So even a graph additive Blend B-slot pointed at one can't rest-pose-
      // subtract it twice. Same treatment at the other two generated-clip
      // registrations above (loadAndRegister tracksPayload, __REGISTER_CUSTOM_BODY_CLIP__).
      window.__REGISTER_GENERATED_BODY_CLIP__ = (clipName, clip) => {
        registerBodyClip(clipName, clip, { additive: false, replace: true, additiveInPlace: true })
      }

      // Register an ALREADY-REGISTERED clip under a SECOND key — no download, no
      // GLB parse, no track prune. The `custom_body_<assetId>` alias every
      // animation asset also resolves by (clipRuntimeKeys.js) names the same
      // bytes as the asset's own name, so re-fetching and re-parsing the GLB to
      // produce it was pure waste: measured on myra (58 body assets), that was
      // 58 uncached downloads plus a SECOND ~1026-track parse+prune each, all
      // on the main thread, during a character switch.
      //
      // The clip is CLONED rather than shared. Two keys pointing at one action
      // object looks tempting and is a bug: applyWeights' __DIRECT_PLAY__ pass
      // stops every action whose key !== dp.clipName, so playing the alias would
      // find the same object under the name key and stop the very action it was
      // about to play. `mixer.clipAction` also caches per clip object, so a
      // distinct action REQUIRES a distinct clip. A clone is array copies — far
      // below a parse — and the alias is opt-in per asset, not per frame.
      window.__ALIAS_BODY_CLIP__ = aliasBodyClip

      stage('Finalising scene…')
      // NOW add the rig to the scene. Everything gated on __AVATAR_READY__
      // (tests, EnvProbe, FaceClipPlayer.loadAll) can safely read
      // __AVATAR_ACTIONS__ / __CLIP_BINDINGS__ after this point.
      group.add(combined)

      // Avatar readiness is owned here, not HeadModel — characters without
      // face blendshapes (no morph-target meshes) still need to signal ready
      // so downstream consumers don't hang. NOTE: readiness now means "rig +
      // materials + PRIORITY clips" — the remaining body clips stream in the
      // background (below) and the graph pulls any it needs on demand
      // (GraphEvaluator's missing-action hook, GestureChannel).
      if (abandoned('ready flip')) return
      window.__AVATAR_READY__ = true

      // Frame the editor camera on the rig's own bounds, and register the same
      // fit for the Frame Avatar / F command. Loading content frames it — a
      // character switch would otherwise inherit the previous character's
      // camera, which for a head-only splat sits centimetres off its face.
      {
        const frameToRig = ({ preserveDirection = true } = {}) => {
          const cam = window.__AVATAR_CAMERA__
          if (!cam) return false
          const box = new THREE.Box3().setFromObject(group)
          if (box.isEmpty()) return false
          const sphere = box.getBoundingSphere(new THREE.Sphere())
          return fitCameraToSphere(
            cam, window.__ORBIT_CONTROLS__,
            [sphere.center.x, sphere.center.y, sphere.center.z], sphere.radius,
            { preserveDirection },
          )
        }
        frameToRig({ preserveDirection: false })
        // No unregister kept: this loader effect has no teardown, and ownership
        // is "last content loaded" — a character switch clears it (App), and the
        // next character's pipeline replaces it.
        setContentFramer(frameToRig)
      }

      // ── Background: stream the remaining clips into the live mixer during
      //    idle frames. requestIdleCallback mirrors FaceClipPlayer's deferred
      //    load; the render loop can jump a clip to the front via request(). ──
      // Pace the background sweep. Each finished GLB triggers a synchronous
      // parse + track-prune on the main thread (cheap on a GPU, ~hundreds of ms
      // under software rendering), so cap concurrency to keep the render loop
      // responsive while clips stream. A small setTimeout gap (rather than
      // requestIdleCallback, which starves under a continuous render loop) keeps
      // the sweep progressing steadily; an on-demand pull bypasses the cap and
      // loads its clip immediately when the graph actually needs it.
      const scheduleGap = (cb) => setTimeout(cb, 40)
      const bodyClipLoader = createBodyClipLoader({ entries: restEntries, loadAndRegister, schedule: scheduleGap, maxConcurrent: 3 })
      setBodyClipLoader(bodyClipLoader)
      window.__BODY_CLIP_LOADER__ = bodyClipLoader
      bodyClipLoader.start()
      devLog(`[AvatarModel] ready with ${priorityEntries.length} priority clip(s); streaming ${restEntries.length} in background`)

      // Live preview: mutate the built THREE materials in place from a
      // partial material_assets.data patch. Returns true if every field
      // was applied live; false if a rebuild is needed (texture binding,
      // class promotion, etc.) — the caller should fall back to
      // __RELOAD_MATERIALS__ in that case.
      window.__LIVE_PREVIEW_MATERIAL__ = (assetId, patch) =>
        livePreviewMaterial(assetId, patch)

      // Material-graph debug + test hook. compile() is pure; lastCompiled is
      // refreshed on each __RELOAD_MATERIALS__ for assertion in tests.
      window.__MATERIAL_GRAPH__ = {
        compile: (graph) => compileMaterialGraph(graph),
        lastCompiled: null,
      }
      window.__SET_GRAPH_PARAM__ = (assetId, nodeId, handle, value) =>
        setGraphParam(assetId, nodeId, handle, value)

      // Live material hot-swap for the authoring panel. Re-fetches the
      // material/texture/mapping rows from the server and rebinds every
      // mesh on the rig — no full page reload.
      window.__RELOAD_MATERIALS__ = async (overrides) => {
        if (!rigSceneRef.current) return
        const slug = overrides?.slug ?? overrides?.id ?? fullCharacter?.id
        if (!slug) return
        try {
          // Mapping source, in priority order: an explicit override (remote
          // co-edit), else the live collab mapping when co-editing (the collab
          // service persists to AAM only debounced, so an AAM re-fetch would be
          // stale and clobber live edits), else AAM. Materials + textures are
          // always fetched — only the mapping is co-edited in Stage 2.
          const mappingSource =
            overrides?.mapping ?? currentCollabMapping() // null unless co-editing
          const [materialRows, textureRows, fetchedMapping] = await Promise.all([
            listMaterials(slug).catch(() => []),
            listTextureAssets(slug).catch(() => []),
            mappingSource ? Promise.resolve(mappingSource) : getMaterialMapping(slug).catch(() => []),
          ])
          const mapping = fetchedMapping
          if (mapping.length === 0 || materialRows.length === 0) return
          // Reloads rebuild straight at the target tier — the texture cache is
          // already warm from the initial cascade, no low-first pass needed.
          textureRowsRef.current = textureRows
          const built = buildMaterialsFromAssets({ materials: materialRows, textures: textureRows, mapping, tier: getTargetTier() })
          applyMaterialMapping(rigSceneRef.current, built)
          const graphMat = built.matById && [...built.matById.values()].find(m => m.userData?.isGraphMaterial)
          if (graphMat) window.__MATERIAL_GRAPH__.lastCompiled = { uniforms: Object.keys(graphMat.userData.graphUniforms) }
        } catch (e) {
          console.warn('[AvatarModel] reload materials failed:', e)
        }
      }

      // Snap the rig's bbox bottom to the floor. Source FBX rigs aren't
      // always authored with feet at y=0 — this rig's wheels extend below
      // the origin, so without an offset she clips into the ground plane.
      // Use setFromObject on `combined` while the group is still at y=0 so
      // we get the rig's rest-pose bbox in local space; SkinnedMesh bounds
      // are geometry-level, not skinned-pose, which is what we want (a
      // stable reference that doesn't bob with animation).
      const bbox = new THREE.Box3().setFromObject(combined)
      const rigMinY = bbox.min.y
      // Floor baseline + the gizmo's vertical lift. Both terms come from
      // avatarFloor so this stays the ONE writer of group.position.y — a gizmo
      // assigning Y itself would be undone by the next floor change.
      //
      // The lift is cleared here because it belongs to the rig that was dragged,
      // not to the module: it is transient runtime placement (the outliner row
      // says "Runtime only — not saved"), so a freshly loaded character must
      // start on the floor rather than inheriting where the last one was lifted
      // to.
      avatarFloor.setLift(0)
      const applyFloor = (y) => { group.position.y = y - rigMinY + avatarFloor.getLift() }
      applyFloor(avatarFloor.getY())
      const unsub = avatarFloor.subscribe(applyFloor)
      window.__AVATAR_GROUP__   = group
      window.__AVATAR_RIG_MIN_Y__ = rigMinY

      loadStateRef.current = { body: true, face: true, boneCount, unsubFloor: unsub }
      stage(null) // clear stage — the overlay disappears once onLoaded fires too
      onLoaded?.({ bodyMeshLoaded: true, faceMeshLoaded: true, skeletonBones: boneCount, morphTargetCount: 0 })

      // console.log(`[AvatarModel] Ready: ${clips.map(c => c.name).join(', ')}`)
    }

    loadAll().catch(err => {
      console.error('[AvatarModel] Load failed:', err)
      // Tag with the character this instance was asked to render so App can drop
      // it if we've since switched away (the NO_RIG path already sets this; this
      // covers network / parse failures too). See isStaleLoadError.
      if (err && err.characterId == null) err.characterId = character?.id
      onError?.(err)
    })

    // Teardown. These globals describe THE LOADED RIG, and this is the only
    // component that mounts one — so when it goes, they have to go with it.
    // Held past the unmount they become a lie rather than a stale value: the
    // outliner derives its Armature / Meshes / Face rig rows from
    // __AVATAR_SCENE__ (lib/outlinerTree.js), and this component never mounts
    // in splat mode, so switching from a GLB character to a splat one left the
    // splat character reporting the PREVIOUS character's joint count, mesh list
    // and morph count as its own — a face with 168 rig controls reading '52
    // shape targets' off a rig that had been unmounted. Cleared, not deleted:
    // the __* debug contract stays, absence just reads as absence.
    return () => {
      cancelled = true
      // Release the re-entrancy latch (see its comment at the top of this
      // effect). Held past the teardown it is no longer a latch but a permanent
      // one-shot: the next effect run on this instance — StrictMode's second
      // mount — is refused and NOTHING loads the rig.
      loadingRef.current = false
      loadStateRef.current.unsubFloor?.()
      loadStateRef.current = { body: false, face: false, boneCount: 0 }
      window.__AVATAR_READY__            = false
      window.__AVATAR_SCENE__            = null
      window.__AVATAR_GROUP__            = null
      window.__AVATAR_RIG_MIN_Y__        = null
      window.__AVATAR_MIXER__            = null
      window.__AVATAR_ACTIONS__          = null
      window.__AVATAR_ADDITIVE_ACTIONS__ = null
      window.__CLIP_BINDINGS__           = null
      window.__BODY_CLIP_LOADER__        = null
      // Same reasoning as the cleared globals above: the next character must not
      // inherit this one's gaze. The pivot cache keys on the root object so it
      // self-heals, but the smoothed aim would otherwise start mid-glance.
      resetEyeFollowScene()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return <group ref={groupRef} />
}
